import { Close, Hide, Search, Show } from '@sds-system/assets';
import type { PolymorphicRef } from '@sds-system/types';
import { useForkRef } from '@sds-system/utils';
import type {
  ChangeEvent,
  ElementType,
  FocusEvent,
  HTMLInputTypeAttribute,
  MouseEvent,
} from 'react';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { Box } from '../../Box';
import { Icon as SDIcon } from '../../Icon';
import { IconButton } from '../../IconButton';
import { Row } from '../../Row';
import { Typography } from '../../Typography';
import {
  StyledTextFieldInput,
  StyledTextFieldRoot,
  StyledTextFieldWrapper,
} from './TextField.styled';
import type { TextFieldComponent, TextFieldProps, TextFieldVariant } from './TextField.types';

export const InnerTextField: TextFieldComponent = forwardRef(
  <C extends ElementType = 'input', U extends TextFieldVariant = 'normal'>(
    {
      size = 'L',
      isReset = true,
      variant,
      onSearch,
      Icon,
      onChange,
      onBlur,
      onFocus,
      disabled = false,
      name,
      unit,
      styledComponents,
      isCursorEnd,
      type,
      onClick,
      hasVisibilityIcon = type === 'password',
      status = 'none',
      ...props
    }: TextFieldProps<C, U>,
    ref: PolymorphicRef<C>,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const handleRef = useForkRef<HTMLInputElement>(inputRef, ref);
    const [isShowReset, setIsShowReset] = useState(false);
    const [innerInputType, setInnerInputType] = useState<HTMLInputTypeAttribute | undefined>(type);
    const [isShowSecret, setIsShowSecret] = useState(false);

    const inputChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value || '';

      if (value === '') {
        setIsShowReset(false);
        setIsShowSecret(false);
      }

      if (value) {
        setIsShowReset(true);
        setIsShowSecret(true);
      }

      if (onChange) onChange(e);
    };

    const inputBlurHandler = (e: FocusEvent<HTMLInputElement>) => {
      if (onBlur) onBlur(e);
      setIsShowReset(false);
      setIsShowSecret(false);
    };

    const inputFocusHandler = (e: FocusEvent<HTMLInputElement>) => {
      const value = e.target.value || '';

      if (isCursorEnd && type !== 'email' && inputRef?.current) {
        const value = inputRef?.current?.value;
        inputRef.current.focus();
        inputRef.current.selectionStart = value.length;
      }

      if (onFocus) onFocus(e);

      if (value) {
        setIsShowReset(true);
        setIsShowSecret(true);
      }
    };

    const inputClickHandler = (e: MouseEvent<HTMLInputElement>) => {
      if (isCursorEnd && type !== 'email' && inputRef?.current) {
        const value = inputRef?.current?.value;
        inputRef.current.focus();
        inputRef.current.selectionStart = value.length;
      }

      if (onClick) onClick(e);
    };

    const resetButtonMouseDownHandler = (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
    };

    const resetButtonClickHandler = () => {
      if (!inputRef?.current) return;
      inputRef.current.value = '';
      setIsShowReset(false);
      setIsShowSecret(false);
      if (onChange)
        onChange({
          target: inputRef.current,
          currentTarget: inputRef.current,
        } as ChangeEvent<HTMLInputElement>);
    };

    const searchButtonClickHandler = (e: MouseEvent<HTMLButtonElement>) => {
      if (variant === 'searchBar')
        (onSearch as (e: MouseEvent<HTMLButtonElement>, target?: HTMLInputElement) => void)(
          e,
          inputRef?.current || undefined,
        );
    };

    const visibilityIconClickHandler = () => {
      setInnerInputType(innerInputType === 'password' ? 'text' : 'password');
    };

    useEffect(() => {
      if (type === innerInputType) return;
      setInnerInputType(type);
    }, [type]);

    return (
      <StyledTextFieldRoot
        className={`textfield-root ${name}-textfield-root`}
        {...styledComponents?.rootProps}
      >
        <StyledTextFieldWrapper
          className='textfield-wrapper'
          disabled={disabled}
          isError={status === 'error'}
          isSuccess={status === 'success'}
          size={size}
          variant={variant}
          {...styledComponents?.wrapperProps}
        >
          <StyledTextFieldInput
            disabled={disabled}
            name={name}
            onBlur={inputBlurHandler}
            onChange={inputChangeHandler}
            onClick={inputClickHandler}
            onFocus={inputFocusHandler}
            ref={handleRef}
            type={hasVisibilityIcon ? innerInputType : type}
            {...props}
            {...styledComponents?.inputProps}
          />
          <Row alignItems='center' gap={8} height='100%' ml={8}>
            {isReset && isShowReset && (
              <IconButton
                className={`textfield-reset-button ${name}-textfield-reset-button`}
                height='100%'
                minHeight='100%'
                onClick={resetButtonClickHandler}
                onMouseDown={resetButtonMouseDownHandler}
                size={variant === 'normal' ? 'XS' : 'S'}
                type='button'
                variant='ghost.assistive'
              >
                <SDIcon color='icon.interactive.secondary' source={Close} />
              </IconButton>
            )}
            {type === 'password' && hasVisibilityIcon && isShowSecret && (
              <IconButton
                height='100%'
                minHeight='100%'
                onClick={visibilityIconClickHandler}
                onMouseDown={(e) => e.preventDefault()}
                size={variant === 'normal' ? 'XS' : 'S'}
                type='button'
                variant='ghost.assistive'
              >
                <SDIcon
                  color='icon.interactive.secondary'
                  source={innerInputType === 'password' ? Show : Hide}
                />
              </IconButton>
            )}
            {Icon && Icon}
            {unit && (
              <Typography
                className={`textfield-unit ${name}-textfield-unit`}
                css={{
                  whiteSpace: 'nowrap',
                  fontSize: 'inherit',
                  fontWeight: 'inherit',
                }}
              >
                {unit}
              </Typography>
            )}
            {variant === 'searchBar' && (
              <Box
                as='button'
                className={`textfield-search-button ${name}-textfield-search-button`}
                flexCenter
                height='100%'
                minWidth={40}
                type='submit'
                width={40}
              >
                <SDIcon
                  color='icon.primary'
                  css={{ cursor: 'pointer' }}
                  onClick={searchButtonClickHandler}
                  size={size === 'M' ? '2XS' : 'XS'}
                  source={Search}
                />
              </Box>
            )}
          </Row>
        </StyledTextFieldWrapper>
      </StyledTextFieldRoot>
    );
  },
);
